'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import Link from 'next/link'
import { useTranslation } from 'react-i18next'
import {
  HiMail,
  HiSearch,
  HiChevronDown,
  HiX,
  HiPaperClip,
} from 'react-icons/hi'
import { FaDiscord } from 'react-icons/fa'
import { useAuth } from '../../contexts/AuthContext'
import {
  DISCORD_URL,
  SUPPORT_CATEGORIES,
  SUPPORT_TOPICS,
  getSupportTopic,
  buildSupportEmail,
} from './supportContent'

// /contact is the single front door for support.
//
// Order of operations is deliberate: answer it (FAQ) → route it (email vs Discord) → only then
// hand over an address. Every other contact entry point on the site (footer, header, drawer,
// settings, the "site is down" article) links here rather than to a raw mailto, so the FAQ gets
// a chance before an email is written. That is what makes the support@ auto-reply — which
// repeated this exact list back at people after they'd already given up — redundant.
//
// Questions are addressable: /contact#refund opens and scrolls to that answer, so support can
// reply with a link instead of pasting boilerplate.

const CARD = 'bg-[var(--middle-bg-color)] rounded-2xl'

const CTA_CLASS =
  'w-full px-6 py-3 text-white rounded-full flex items-center justify-center font-semibold transition-opacity'

// mailto: URLs get truncated by some clients well before the RFC limit, and the details block
// eats a few hundred characters on its own. This keeps the whole draft comfortably inside that.
const MESSAGE_LIMIT = 1200

// What the Discord is actually good for. Concrete beats "join our community" — it also draws
// the line the email card doesn't: none of these are things support can answer faster.
const DISCORD_TOPICS = [
  { emoji: '🎯', key: 'support_discordItem_prompts' },
  { emoji: '🎬', key: 'support_discordItem_video' },
  { emoji: '🧬', key: 'support_discordItem_characters' },
  { emoji: '🖼️', key: 'support_discordItem_showcase' },
  { emoji: '📣', key: 'support_discordItem_updates' },
]

const Faq = ({ faq, open, onToggle, t, safe, onPickTopic }) => {
  const actions = (faq.actions || []).filter(
    (action) => !(action.nsfwOnly && safe),
  )

  return (
    <div
      id={faq.id}
      className={`overflow-hidden rounded-xl border transition-colors scroll-mt-24 ${
        open
          ? 'border-[var(--main-bg-color)] bg-[var(--foreground-bg-color)]'
          : 'border-white/5 bg-[var(--foreground-bg-color)]/60 hover:border-white/20'
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={`${faq.id}-answer`}
        className="w-full flex items-center gap-3 px-4 py-3.5 text-left"
      >
        <span
          aria-hidden="true"
          className="shrink-0 grid place-items-center w-9 h-9 rounded-lg bg-[var(--secondary-bg-color)] text-lg leading-none"
        >
          {faq.emoji}
        </span>
        <span className="flex-1 font-semibold text-white text-[15px] leading-snug">
          {t(faq.qKey)}
        </span>
        <HiChevronDown
          size={20}
          aria-hidden="true"
          className={`shrink-0 text-gray-400 transition-transform ${
            open ? 'rotate-180 text-white' : ''
          }`}
        />
      </button>

      {open && (
        <div id={`${faq.id}-answer`} className="px-4 pb-4 pl-16">
          <p className="text-[15px] leading-relaxed text-gray-300 whitespace-pre-line">
            {t(faq.aKey)}
          </p>

          {actions.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {actions.map((action) => {
                const label = action.labelKey
                  ? t(action.labelKey)
                  : action.label
                const className =
                  'inline-flex items-center rounded-full bg-[var(--secondary-bg-color)] hover:bg-[var(--main-bg-color)] px-3.5 py-1.5 text-sm font-semibold text-white transition-colors'

                // Sends them to the composer with the topic already picked, rather than
                // opening an empty draft they'd have to write from scratch anyway.
                if (action.topic) {
                  return (
                    <button
                      key={label}
                      type="button"
                      onClick={() => onPickTopic(action.topic)}
                      className={className}
                    >
                      <HiMail size={16} className="mr-1.5" aria-hidden="true" />
                      {label}
                    </button>
                  )
                }

                if (action.external) {
                  return (
                    <a
                      key={label}
                      href={action.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={className}
                    >
                      {label}
                    </a>
                  )
                }

                return (
                  <Link key={label} href={action.href} className={className}>
                    {label}
                  </Link>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const TheContactPage = () => {
  const { t } = useTranslation()
  const { user, email, plan, safe } = useAuth()

  const [query, setQuery] = useState('')
  const [category, setCategory] = useState('all')
  const [openIds, setOpenIds] = useState([])
  const [closedIds, setClosedIds] = useState([])
  const [topic, setTopic] = useState('other')
  const [message, setMessage] = useState('')

  const composerRef = useRef(null)
  const messageRef = useRef(null)

  const canSend = message.trim().length > 0

  const draft = buildSupportEmail(topic, {
    uid: user?.uid,
    email,
    plan,
    message,
  })

  // An "Email us" chip inside an answer preselects the topic and drops the user into the
  // composer with the cursor already in the message box.
  const pickTopic = (topicId) => {
    setTopic(topicId)
    composerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setTimeout(() => messageRef.current?.focus({ preventScroll: true }), 400)
  }

  // /contact#refund → open that answer and scroll to it, so a support reply can be a link.
  useEffect(() => {
    const id = window.location.hash.replace('#', '')
    if (!id) return
    const known = SUPPORT_CATEGORIES.some((c) =>
      c.faqs.some((faq) => faq.id === id),
    )
    if (!known) return
    setOpenIds([id])
    requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ block: 'center' })
    })
  }, [])

  const searching = query.trim().length > 1

  const categories = useMemo(() => {
    const needle = query.trim().toLowerCase()

    return SUPPORT_CATEGORIES.map((cat) => {
      const faqs = cat.faqs.filter((faq) => {
        if (searching) {
          return `${t(faq.qKey)} ${t(faq.aKey)}`.toLowerCase().includes(needle)
        }
        return category === 'all' || category === cat.id
      })
      return { ...cat, faqs }
    }).filter((cat) => cat.faqs.length > 0)
  }, [query, category, searching, t])

  const faqSchema = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: SUPPORT_CATEGORIES.flatMap((cat) =>
        cat.faqs.map((faq) => ({
          '@type': 'Question',
          name: t(faq.qKey),
          acceptedAnswer: { '@type': 'Answer', text: t(faq.aKey) },
        })),
      ),
    }),
    [t],
  )

  // Search results come pre-opened — reading the answer is the point of searching — so while a
  // query is active the toggle tracks what's been closed rather than what's been opened.
  const isOpen = (id) =>
    searching ? !closedIds.includes(id) : openIds.includes(id)

  const toggle = (id) => {
    const flip = (ids) =>
      ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]
    return searching ? setClosedIds(flip) : setOpenIds(flip)
  }

  return (
    <div className="bg-[var(--background-bg-color)] min-h-screen">
      <title>Promptchan AI - Help & Support</title>
      <meta
        name="description"
        content="Get help with Promptchan AI. Answers on refunds, subscriptions, Gems, account deletion and generation issues, plus email support and our Discord community."
      />
      <meta
        name="keywords"
        content="Promptchan AI, Support, Help, FAQ, Refund, Cancel Subscription, Contact, Customer Service"
      />
      <link rel="canonical" href="https://promptchan.com/contact" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(faqSchema).replace(/</g, '\\u003c'),
        }}
      />

      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-16 sm:py-24 text-white">
        {/* Hero */}
        <div className="text-center">
          <h1 className="text-3xl sm:text-5xl font-semibold tracking-tight">
            {t('support_heroTitle')}
          </h1>
          <p className="mt-4 text-base sm:text-lg text-gray-300">
            {t('support_heroSubtitle')}
          </p>
        </div>

        {/* Search */}
        <div className="mt-8 relative">
          <HiSearch
            size={20}
            aria-hidden="true"
            className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('support_searchPlaceholder')}
            aria-label={t('support_searchPlaceholder')}
            className="w-full rounded-full bg-[var(--middle-bg-color)] border border-white/10 focus:border-[var(--main-bg-color)] outline-none py-3.5 pl-12 pr-12 text-white placeholder-gray-500"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              aria-label={t('support_clearSearch')}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
            >
              <HiX size={20} />
            </button>
          )}
        </div>

        {/* Category filter */}
        {!searching && (
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            {[{ id: 'all', titleKey: 'support_cat_all' }]
              .concat(SUPPORT_CATEGORIES)
              .map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setCategory(cat.id)}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                    category === cat.id
                      ? 'bg-[var(--main-bg-color)] text-white'
                      : 'bg-[var(--middle-bg-color)] text-gray-300 hover:text-white hover:bg-[var(--foreground-bg-color)]'
                  }`}
                >
                  {cat.emoji ? `${cat.emoji} ` : ''}
                  {t(cat.titleKey)}
                </button>
              ))}
          </div>
        )}

        {/* Answers */}
        <div className="mt-8 space-y-8">
          {categories.map((cat) => (
            <section key={cat.id}>
              <h2 className="flex items-center gap-2 px-1 pb-3 text-sm font-semibold uppercase tracking-wider text-gray-400">
                <span aria-hidden="true">{cat.emoji}</span>
                {t(cat.titleKey)}
              </h2>
              <div className={`${CARD} p-2 space-y-2`}>
                {cat.faqs.map((faq) => (
                  <Faq
                    key={faq.id}
                    faq={faq}
                    open={isOpen(faq.id)}
                    onToggle={() => toggle(faq.id)}
                    t={t}
                    safe={safe}
                    onPickTopic={pickTopic}
                  />
                ))}
              </div>
            </section>
          ))}

          {categories.length === 0 && (
            <div className={`${CARD} p-8 text-center`}>
              <p className="text-4xl" aria-hidden="true">
                🔍
              </p>
              <p className="mt-3 text-gray-300">{t('support_noResults')}</p>
            </div>
          )}
        </div>

        {/* Routing */}
        <div className="mt-16 text-center">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight">
            {t('support_stillStuckTitle')}
          </h2>
        </div>

        <div className={`mt-6 grid gap-4 ${safe ? '' : 'md:grid-cols-2'}`}>
          {/* Email — anything tied to an account */}
          <div
            ref={composerRef}
            className={`${CARD} p-6 border border-[var(--main-bg-color)]/40`}
          >
            <div className="flex items-center gap-3">
              <span
                aria-hidden="true"
                className="grid place-items-center w-11 h-11 rounded-xl bg-[var(--main-bg-color)] text-xl"
              >
                ✉️
              </span>
              <div>
                <p className="font-semibold text-lg leading-tight">
                  {t('support_emailTitle')}
                </p>
                <p className="text-sm text-gray-400">
                  {t('support_emailTagline')}
                </p>
              </div>
            </div>

            <p className="mt-5 text-xs font-semibold uppercase tracking-wider text-gray-400">
              {t('support_emailTopicLabel')}
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {SUPPORT_TOPICS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setTopic(option.id)}
                  aria-pressed={topic === option.id}
                  className={`rounded-full px-3 py-1.5 text-sm font-semibold transition-colors ${
                    topic === option.id
                      ? 'bg-[var(--main-bg-color)] text-white'
                      : 'bg-[var(--secondary-bg-color)] text-gray-300 hover:text-white'
                  }`}
                >
                  {t(option.labelKey)}
                </button>
              ))}
            </div>

            {/* The message is written here, not in the mail client: by the time the draft opens
                it is finished, and the account details are already appended. */}
            <p className="mt-5 text-xs font-semibold uppercase tracking-wider text-gray-400">
              {t('support_emailMessageLabel')}
            </p>
            <textarea
              ref={messageRef}
              rows={5}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={MESSAGE_LIMIT}
              placeholder={t(getSupportTopic(topic).placeholderKey)}
              aria-label={t('support_emailMessageLabel')}
              className="mt-2 w-full resize-y rounded-xl bg-[var(--foreground-bg-color)] border border-white/10 focus:border-[var(--main-bg-color)] outline-none px-4 py-3 text-sm text-white placeholder-gray-500"
            />

            {/* No file picker here: a mailto: cannot carry an attachment, and the share-sheet
                workaround composes a message with no recipient. Asking for the screenshot in
                the mail client is the only route that actually reaches us. */}
            <div className="mt-1.5 flex items-start justify-between gap-3">
              <span className="flex items-start gap-1.5 text-xs text-gray-500">
                <HiPaperClip
                  size={13}
                  className="mt-0.5 shrink-0"
                  aria-hidden="true"
                />
                {t('support_emailScreenshotHint')}
              </span>
              <span className="shrink-0 text-xs text-gray-500">
                {message.length}/{MESSAGE_LIMIT}
              </span>
            </div>

            {canSend ? (
              <a
                href={draft.mailto}
                className={`${CTA_CLASS} mt-4 bg-[var(--main-bg-color)] hover:opacity-90`}
              >
                {t('emailSupport')}
                <HiMail size={20} className="ml-2" aria-hidden="true" />
              </a>
            ) : (
              <button
                type="button"
                disabled
                className={`${CTA_CLASS} mt-4 bg-[var(--main-bg-color)] opacity-40 cursor-not-allowed`}
              >
                {t('emailSupport')}
                <HiMail size={20} className="ml-2" aria-hidden="true" />
              </button>
            )}

            <p className="mt-3 text-xs text-gray-400">
              {canSend
                ? t('support_emailNote')
                : t('support_emailMessageRequired')}
            </p>
          </div>

          {/* Discord — community craft help, hidden in safe mode like everywhere else */}
          {!safe && (
            <div
              className={`${CARD} p-6 border border-[#5865F2]/40 flex flex-col`}
            >
              <div className="flex items-center gap-3">
                <span
                  aria-hidden="true"
                  className="grid place-items-center w-11 h-11 rounded-xl bg-[#5865F2]"
                >
                  <FaDiscord size={22} className="text-white" />
                </span>
                <div>
                  <p className="font-semibold text-lg leading-tight">
                    {t('support_discordTitle')}
                  </p>
                  <p className="text-sm text-gray-400">
                    {t('support_discordTagline')}
                  </p>
                </div>
              </div>

              <p className="mt-5 text-xs font-semibold uppercase tracking-wider text-gray-400">
                {t('support_discordListLabel')}
              </p>
              <ul className="mt-3 space-y-2.5 text-sm text-gray-300">
                {DISCORD_TOPICS.map((item) => (
                  <li key={item.key} className="flex items-start gap-2.5">
                    <span
                      aria-hidden="true"
                      className="shrink-0 grid place-items-center w-7 h-7 rounded-lg bg-[var(--secondary-bg-color)] text-sm leading-none"
                    >
                      {item.emoji}
                    </span>
                    <span className="pt-1 leading-snug">{t(item.key)}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-auto pt-6">
                <a
                  href={DISCORD_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${CTA_CLASS} bg-[#5865F2] hover:opacity-90`}
                >
                  {t('support_discordCta')}
                  <FaDiscord size={20} className="ml-2" aria-hidden="true" />
                </a>
                <p className="mt-3 text-xs text-gray-400">
                  {t('support_discordNote')}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default TheContactPage
